import { useNavigate } from 'react-router-dom';
import { getAffairOwnerName, formatAffairAmount, formatAffairDate, getAffairPartnerName } from '../affairFormatters';
import { getAffairStage } from '../affairModel';
import AffairStageActions from './AffairStageActions';

const DAY_MS = 24 * 60 * 60 * 1000;
const STALE_ACTIVITY_DAYS = 14;

const read = (affair, ...keys) => keys.map((key) => affair?.[key]).find((value) => value !== undefined && value !== null);
const getRealizations = (affair) => affair.realizations || [
  ...(affair.projects || affair.Projects || []),
  ...(affair.interventions || affair.Interventions || []),
];

const getDeadlineAlert = (deadline) => {
  if (!deadline) return null;
  const dueAt = new Date(deadline).getTime();
  if (Number.isNaN(dueAt)) return null;
  const days = Math.ceil((dueAt - Date.now()) / DAY_MS);
  if (days < 0) return { key: 'overdue', label: 'Échéance dépassée', level: 'danger' };
  if (days <= 3) return { key: 'due-soon', label: 'Échéance sous 3 jours', level: 'warning' };
  return null;
};

export const getAffairCardAlerts = (affair) => {
  const alerts = [];
  const deadlineAlert = getDeadlineAlert(read(affair, 'responseDeadline', 'response_deadline'));
  if (deadlineAlert) alerts.push(deadlineAlert);

  const lastActivity = read(affair, 'lastActivityAt', 'last_activity_at', 'lastActivityDate', 'last_activity_date');
  const lastActivityAt = lastActivity ? new Date(lastActivity).getTime() : NaN;
  if (!Number.isFinite(lastActivityAt) || Date.now() - lastActivityAt > STALE_ACTIVITY_DAYS * DAY_MS) {
    alerts.push({ key: 'stale', label: 'Aucune activité récente', level: 'warning' });
  }
  if (!read(affair, 'ownerUserId', 'owner_user_id', 'Owner', 'owner')) {
    alerts.push({ key: 'owner', label: 'Sans responsable', level: 'warning' });
  }
  const convertedItems = getRealizations(affair);
  if (affair.stage === 'won' && convertedItems.length === 0) {
    alerts.push({ key: 'conversion', label: 'Gagnée non convertie', level: 'info' });
  }
  return alerts;
};



export default function AffairCard({ affair, action, onRequestTransition }) {
  const navigate = useNavigate();
  const stage = getAffairStage(affair.stage);
  const alerts = getAffairCardAlerts(affair);
  const isBusy = action?.affairId === affair.id;
  const detailPath = `/affaires/${affair.id}`;
  const convertedItems = getRealizations(affair);

  const openDetail = () => navigate(detailPath);
  const stop = (callback) => (event) => {
    event.stopPropagation();
    callback(event);
  };

  return (
    <article className="affair-card" onClick={openDetail} onKeyDown={(event) => event.key === 'Enter' && openDetail()} role="link" tabIndex={0} aria-label={`Ouvrir l’affaire ${affair.name}`}>
      <header className="affair-card__header">
        <span className="affair-card__code">{affair.code || `AFF-${affair.id}`}</span>
        <span className={`affair-card__priority affair-card__priority--${affair.priority || 'normal'}`}>{affair.priority || 'normal'}</span>
      </header>
      <h3 className="affair-card__title">{affair.name}</h3>
      <p className="affair-card__partner">{getAffairPartnerName(affair)}</p>
      <dl className="affair-card__facts">
        <div><dt>Responsable</dt><dd>{getAffairOwnerName(affair)}</dd></div>
        <div><dt>Échéance</dt><dd>{formatAffairDate(read(affair, 'responseDeadline', 'response_deadline'))}</dd></div>
        <div><dt>Activité</dt><dd>{formatAffairDate(read(affair, 'lastActivityAt', 'last_activity_at', 'lastActivityDate', 'last_activity_date'))}</dd></div>
        <div><dt>Montant</dt><dd>{formatAffairAmount(read(affair, 'estimatedAmount', 'estimated_amount'))}</dd></div>
        <div title="Estimation de la probabilité de gain"><dt>Probabilité</dt><dd>{read(affair, 'probabilityPercent', 'probability_percent') ?? 0} %</dd></div>
        <div title="Aide à l’évaluation du dossier, distincte de la probabilité"><dt>Score d’Affaire</dt><dd>{read(affair, 'affairScore', 'affair_score') ?? '—'}{read(affair, 'affairScore', 'affair_score') != null ? ' / 100' : ''}</dd></div>
      </dl>
      <div className="affair-card__badges">
        <span className="affair-card__stage">{stage.label}</span>
        {alerts.map((alert) => <span className={`affair-card__alert affair-card__alert--${alert.level}`} key={alert.key}>{alert.label}</span>)}
      </div>
      <div className="affair-card__legacy-actions" onClick={(event) => event.stopPropagation()}>
        <AffairStageActions affair={affair} disabled={isBusy} onTransition={onRequestTransition} />
        <button className="basic-button" type="button" onClick={stop(() => navigate(`/Devis?affairId=${affair.id}`))}>Devis</button>
        {affair.stage === 'won' && convertedItems.length === 0 && <button className="basic-button active" type="button" onClick={stop(() => navigate(`/affaires/${affair.id}?tab=project`))} disabled={isBusy}>Créer la réalisation</button>}
      </div>
    </article>
  );
}
